import localeText from '@/locales/datagrid'
import { FileSpreadsheetIcon } from 'lucide-react'
import useDataGridPro from '../../hooks/useDataGridPro'
import { useTableApiContext } from '../../utils/table-context'
import { ValidRowModel } from '../../utils/types'
import { GridButton, GridMenuItem } from './MenuComponents'

export function handleExportCSV({
    rows,
    columns,
    fileName = 'exportacao',
    getRowsToExport,
}: {
    rows: ValidRowModel[]
    columns: ReturnType<typeof useDataGridPro>['columns']
    fileName?: string
    getRowsToExport?: ReturnType<typeof useDataGridPro>['getRowsToExport']
}) {
    if (getRowsToExport) rows = getRowsToExport(rows) as ValidRowModel[]

    const getCellValue = (id: any, field: any) => {
        const rowData = rows.find((item: any) => item.id === id)
        let value = rowData?.[field]
        const valueGetterFunction = columns.find(
                column => column.field === field,
            )?.valueGetter,
            valueFormatterFunction = columns.find(
                column => column.field === field,
            )?.valueFormatter
        if (valueGetterFunction)
            value = valueGetterFunction({
                row: rowData,
                value,
                field,
                id,
            } as any)
        if (valueFormatterFunction)
            value = valueFormatterFunction({ value, field, id } as any)
        if (value === null || value === undefined) return ''

        // Escapar valores que começam com caracteres que o Excel interpreta como fórmulas
        const stringValue = String(value)
        if (stringValue.match(/^[+=@-]/)) {
            // Usar tab antes do valor para forçar interpretação como texto
            return `"\t${stringValue}"`
        }

        // matches 00:00
        if (stringValue.match(/^(\d{2,3}:\d{2})$/)) {
            return `${stringValue}\u00A0`
        }

        return `"${stringValue}"`
    }

    return new Promise((resolve, reject) => {
        try {
            const rowsToExport = rows.map((item: any) => item.id)
            const headers = columns
                .filter(
                    column =>
                        column.disableExport !== true && column.hide !== false,
                )
                .map(({ field, headerName }) => ({
                    field,
                    headerName,
                }))
            const csvContent = [
                headers
                    .map(header => `${header.headerName || header.field}`)
                    .join(';'),
                ...rowsToExport.map(id =>
                    headers
                        .map(header => getCellValue(id, header.field))
                        .join(';'),
                ),
            ].join('\n')

            // Adicionar BOM (Byte Order Mark) para que o Excel reconheça UTF-8
            const bom = '\uFEFF'
            const csvContentWithBom = bom + csvContent

            const csvDataUri = `data:text/csv;charset=utf-8,${encodeURIComponent(
                csvContentWithBom,
            )}`
            const link = document.createElement('a')
            link.setAttribute('href', csvDataUri)
            link.setAttribute('download', fileName)
            link.click()

            resolve(true)
        } catch (error) {
            reject(error)
        }
    })
}

interface IProps {
    mi?: boolean
    fileName?: string
    label?: string
}

/**
 * Precisa estar no contexto do TableApiProvider
 */
export default function ExportarCsv({ mi, fileName, label }: IProps) {
    const { table, columns, getRowsToExport } = useTableApiContext()

    // Get all rows including expanded ones from TanStack table
    const allRows =
        table?.getExpandedRowModel().flatRows.map(row => row.original) || []
    const columnsToExport = columns.filter(
        col => col.disableExport !== true && col.hide !== true,
    )

    if (mi)
        return (
            <GridMenuItem
                onClick={() =>
                    handleExportCSV({
                        rows: allRows,
                        columns: columnsToExport,
                        fileName,
                        getRowsToExport,
                    })
                }
                startIcon={<FileSpreadsheetIcon className="mr-2" />}
                label={label ?? localeText.toolbarExportCSV}
            />
        )

    return (
        <GridButton
            onClick={() =>
                handleExportCSV({
                    rows: allRows,
                    columns: columnsToExport,
                    fileName,
                    getRowsToExport,
                })
            }
            startIcon={<FileSpreadsheetIcon />}
            label={label ?? localeText.toolbarExportCSV}
        />
    )
}
